import { Fragment } from "react";
import type {
  Element,
  EmbedCode,
  Section,
  seeAlso,
  Storyline,
  Tag,
} from "@app/types/Cue";
import { Type } from "@app/types/enums";
import { ExternalLink, Note } from "@blocks/Blocks";
import { useArticleElements } from "@hooks/useArticleElements";
import AnnotationComponentStockPicker from "@pages/Article/components/StoryElements/Annotation/components/AnnotationComponentStockPicker";
import { BrightcoveVideo } from "@pages/Article/components/StoryElements/BrightcoveVideo";
import { getResolvedArticleElements } from "@pages/Article/utils/helpers";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import cx from "classnames";

import ArticleInlineImage from "./StoryElements/ArticleInlineImage";
import Box from "./StoryElements/Box";
import DataWrapper from "./StoryElements/DataWrapper";
import DynamicIframeWrapper from "./StoryElements/DynamicIframe/DynamicIframeWrapper";
import Embed from "./StoryElements/Embed";
import ListBulleted from "./StoryElements/ListBulleted";
import ListNumbered from "./StoryElements/ListNumbered";
import OmnyPodcast from "./StoryElements/OmnyPodcast";
import ParagraphWrapper from "./StoryElements/ParagraphWrapper";
import PodcastWrapper from "./StoryElements/PodcastWrapper";
import PullQuote from "./StoryElements/PullQuote";
import SubHeading from "./StoryElements/SubHeading";
import Table from "./StoryElements/Table";
import YouTubeVideo from "./StoryElements/YouTubeVideo";

export type ArticleParagraphsProps = {
  className?: string;
  id: string;
  elements: Element[];
  seeAlso?: seeAlso;
  embedCodes?: EmbedCode[];
  displayRelatedLinksAndNewsletterSignupForm?: boolean;
  keywords?: Tag[];
  sections?: Section[];
  index?: number;
  gsChannelsNames?: string[];
  storyline: Storyline;
  isPremium: boolean;
  isBoxWrapped?: boolean;
  isGifted?: boolean;
};

export default function ArticleParagraphs({
  id,
  className,
  elements = [],
  seeAlso,
  displayRelatedLinksAndNewsletterSignupForm = false,
  keywords = [],
  sections = [],
  index = 0,
  gsChannelsNames = [],
  storyline,
  isPremium,
  isBoxWrapped = false,
  isGifted = false,
}: ArticleParagraphsProps): React.ReactElement | null {
  const OktaUserInfo = useOKTAUserStore((store) => store.userInfo);
  const isSubscriber = OktaUserInfo?.usertype === OKTAUserTypeEnum.SUBSCRIBER;

  const fetchedElements = useArticleElements(id, elements);
  const articleElements = getResolvedArticleElements(
    storyline,
    fetchedElements,
    isPremium,
    isSubscriber,
    isGifted
  );

  let paragraphCount = 1;
  const isDisplayImu1 = elements.length > 3 && !isPremium;

  return (
    <div
      className={cx("relative", className)}
      data-testid="article-paragraphs-component"
    >
      {articleElements.map((element) => {
        switch (element.type) {
          case Type.SubHead:
            return (
              <SubHeading
                key={element.id}
                fields={element.fields}
                className="mb-6"
              />
            );

          case Type.Paragraph:
            return (
              <ParagraphWrapper
                key={element.id}
                {...{
                  element,
                  paragraphCount: paragraphCount++,
                  index,
                  id,
                  keywords,
                  gsChannelsNames,
                  displayRelatedLinksAndNewsletterSignupForm,
                  seeAlso,
                  sections,
                  isDisplayImu1,
                  isBoxWrapped,
                }}
              />
            );

          case Type.PodcastWrapper:
            return (
              <PodcastWrapper
                key={element.id}
                fields={element.fields}
                className="mb-6"
              />
            );

          case Type.Image:
            return (
              <Fragment key={element.id}>
                {element.relation ? (
                  <ArticleInlineImage
                    fields={element.fields}
                    relation={element.relation}
                    className="mb-6"
                  />
                ) : null}
              </Fragment>
            );

          case Type.BrightcoveVideo:
            return (
              <BrightcoveVideo
                key={element.id}
                fields={element.fields}
                className="mb-6"
              />
            );

          case Type.DataWrapper:
            return (
              <DataWrapper
                key={element.id}
                fields={element.fields}
                className="mb-6"
              />
            );

          case Type.DynamicIframeWrapper:
            return (
              <DynamicIframeWrapper
                key={element.id}
                fields={element.fields}
                className="mb-6"
              />
            );

          case Type.Graphic:
            return (
              <div className="mb-6" data-story-element={Type.Graphic}>
                <iframe
                  key={element.id}
                  src={element.relation?.href}
                  style={{ width: "100%", minHeight: 300 }}
                  title={element.id}
                />
              </div>
            );

          case Type.Note:
            return (
              <Note
                key={element.id}
                value={element?.fields?.map((f) => f.value).join("\n")}
                className="mb-6"
              />
            );

          case Type.GroupedRelation:
            return (
              <ExternalLink
                key={element.id}
                // @ts-expect-error incomplete typedef
                src={element.children.map((group: unknown) => {
                  const rel = articleElements?.find?.((el) => el.id === group);

                  return rel?.relation?.id;
                })}
              />
            );

          case Type.Embed:
            return (
              <Embed
                key={element.id}
                fields={element.fields}
                className="mb-6"
              />
            );

          case Type.ListBulleted:
            return (
              <ListBulleted
                key={element.id}
                elements={element.reference || []}
                className="mb-6"
              />
            );

          case Type.ListNumbered: {
            return (
              <ListNumbered
                key={element.id}
                elements={element.reference || []}
                className="mb-6"
              />
            );
          }

          case Type.PullQuote:
            return (
              <PullQuote
                key={element.id}
                fields={element.fields}
                className="mb-6"
              />
            );

          case Type.Table:
            return (
              <Table
                key={element.id}
                fields={element.fields}
                className="mb-6"
              />
            );

          case Type.HorizontalRow:
            return (
              <hr className="mb-6" data-story-element={Type.HorizontalRow} />
            );

          case Type.YoutubeVideo:
            return (
              <YouTubeVideo
                key={element.id}
                fields={element.fields}
                className="mb-6"
              />
            );

          case Type.OmnyPodcast:
            return (
              <OmnyPodcast
                key={element.id}
                fields={element.fields}
                className="mb-6"
              />
            );

          case Type.Box:
            return (
              <Fragment key={element.id}>
                {element.reference && element.children ? (
                  <Box
                    elements={elements}
                    storyline={element.children}
                    className="mb-6"
                  />
                ) : null}
              </Fragment>
            );

          case Type.BoxTitle:
            return (
              <p key={element.id} className="mb-6 font-bold">
                {element.fields?.[0]?.value || ""}
              </p>
            );

          case Type.StockPicker:
            return (
              <AnnotationComponentStockPicker
                element={element}
                className="mb-6 inline-block"
              >
                {element.fields?.[0]?.value || ""}
              </AnnotationComponentStockPicker>
            );

          default:
            return null;
        }
      })}

      {isPremium && !isSubscriber ? (
        <div
          className="absolute inset-0 bg-gradient-to-t from-white to-transparent"
          data-testid="white-gradient-overlay"
        ></div>
      ) : null}
    </div>
  );
}
